import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import type { AssetsShareTokenSchema } from '@iconik-io/iconik-api/src/services/assets';

const logger = DebugLogger.getInstance('share_token_manager');

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

export interface ShareTokenRecord {
  shareId: string;
  token: AssetsShareTokenSchema;
  expiresAt: number;
}

export class ShareTokenManager {
  static #STORAGE_KEY = 'magic_link_share_tokens';
  static #MAX_TOKENS = 200;
  #tokenRecords: ShareTokenRecord[] = [];

  constructor() {
    this.#initialize();
  }

  #initialize(): void {
    this.#loadFromStorage();
    this.cleanup();
  }

  #loadFromStorage(): void {
    try {
      const storedData = localStorage.getItem(ShareTokenManager.#STORAGE_KEY);
      if (storedData) {
        this.#tokenRecords = JSON.parse(storedData);
      }
    } catch (error) {
      logger.debug('Failed to load share tokens from local storage:', error);
      this.#tokenRecords = [];
    }
  }

  #saveToStorage(): void {
    try {
      localStorage.setItem(
        ShareTokenManager.#STORAGE_KEY,
        JSON.stringify(this.#tokenRecords),
      );
    } catch (error) {
      logger.debug('Failed to save share tokens to local storage:', error);
    }
  }

  cleanup(): void {
    const now = Date.now();
    const originalCount = this.#tokenRecords.length;

    // Remove expired tokens
    this.#tokenRecords = this.#tokenRecords.filter((record) => {
      const isExpired = record.expiresAt <= now;
      if (isExpired) {
        logger.debug(`Cleaning up expired token for share ${record.shareId}`);
      }
      return !isExpired;
    });

    // Enforce maximum token limit by removing the oldest tokens
    if (this.#tokenRecords.length > ShareTokenManager.#MAX_TOKENS) {
      const excessCount =
        this.#tokenRecords.length - ShareTokenManager.#MAX_TOKENS;

      // Sort by expiresAt (oldest first) and remove excess
      this.#tokenRecords = this.#tokenRecords
        .toSorted((a, b) => a.expiresAt - b.expiresAt)
        .slice(excessCount);

      logger.debug(
        `Cleaned up ${excessCount} oldest token(s) to enforce limit of ${ShareTokenManager.#MAX_TOKENS}`,
      );
    }

    if (this.#tokenRecords.length !== originalCount) {
      this.#saveToStorage();
      logger.debug(
        `Cleaned up ${originalCount - this.#tokenRecords.length} token(s) total`,
      );
    }
  }

  getTokenRecord(shareId: string): ShareTokenRecord | undefined {
    const record = this.#tokenRecords.find((item) => item.shareId === shareId);

    if (!record) {
      return undefined;
    }

    if (record.expiresAt <= Date.now()) {
      this.removeTokenRecord(shareId);
      return undefined;
    }

    return record;
  }

  setToken(token: AssetsShareTokenSchema): void {
    const threeDaysFromNow = Date.now() + THREE_DAYS_MS;

    const newRecord: ShareTokenRecord = {
      shareId: token.share_id,
      token,
      expiresAt: threeDaysFromNow,
    };

    const existingIndex = this.#tokenRecords.findIndex(
      (item) => item.shareId === token.share_id,
    );

    if (existingIndex >= 0) {
      this.#tokenRecords[existingIndex] = newRecord;
    } else {
      this.#tokenRecords.push(newRecord);
    }

    this.#saveToStorage();
  }

  removeTokenRecord(shareId: string): void {
    const initialLength = this.#tokenRecords.length;
    this.#tokenRecords = this.#tokenRecords.filter(
      (item) => item.shareId !== shareId,
    );

    if (this.#tokenRecords.length !== initialLength) {
      this.#saveToStorage();
    }
  }

  clearAll(): void {
    this.#tokenRecords = [];
    localStorage.removeItem(ShareTokenManager.#STORAGE_KEY);
  }
}

export const shareTokenManager = new ShareTokenManager();
